import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { AppFeatures, Editor, EditorForResource, Path, ResourceForEditor } from '@s8l/client-tree-lib';
import { BehaviorSubject } from 'rxjs';

import { CmsService } from './cms.service';

interface NodeEditorConfig {
  feature?: AppFeatures[];
  mode: Editor;
  icon: string;
  label: string;
}

interface NodeEditorRouteData {
  mode?: string;
  path?: Path;
  create: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NodeEditorService implements CanActivateChild {
  public path = new BehaviorSubject<Path>(new Path([]));
  public mode = new BehaviorSubject<string>(Editor.Node);
  // is this a route that can create a resource?
  public createRoute = new BehaviorSubject<boolean>(false);

  private editors: NodeEditorConfig[] = [
    {
      mode: Editor.Node,
      icon: 'edit',
      label: 'Node'
    },
    {
      feature: [AppFeatures.QuizSp, AppFeatures.QuizMp],
      mode: Editor.Quiz,
      icon: 'question-circle',
      label: 'Quiz'
    },
    {
      mode: Editor.Wiki,
      icon: 'book-alt',
      label: 'Wiki'
    },
    {
      feature: [AppFeatures.Glossary],
      mode: Editor.Glossary,
      icon: 'search-plus',
      label: 'Glossary'
    },
    {
      feature: [AppFeatures.Impuls],
      mode: Editor.Impuls,
      icon: 'list',
      label: 'Impuls'
    },
    {
      feature: [AppFeatures.Casestudy],
      mode: Editor.Casestudy,
      icon: 'file-edit',
      label: 'Case Study'
    },
    {
      mode: Editor.Media,
      icon: 'image',
      label: 'Media'
    },
    {
      feature: [AppFeatures.Vimeo],
      mode: Editor.Video,
      icon: 'film',
      label: 'Video'
    },
    {
      feature: [AppFeatures.CustomContent],
      mode: Editor.CustomContent,
      icon: 'file-code',
      label: 'Custom Content'
    }
  ];

  public get enabledEditors(): NodeEditorConfig[] {
    return this.editors.filter(e => {
      if (!e.feature) {
        return true;
      }
      // Enable editor if at least one of the features is enabled
      return e.feature.some(f => this.cms.hasFeatures([f]));
    });
  }

  constructor(private cms: CmsService, private router: Router) {}

  private findRouteData(route: ActivatedRouteSnapshot): NodeEditorRouteData {
    let res: NodeEditorRouteData = {
      path: new Path([]),
      create: false
    };

    if (route.params['path']) {
      res.path = Path.parse(route.params['path']);
    }

    if (route.data['mode']) {
      res.mode = route.data['mode'];
    }

    if (route.data['create']) {
      res.create = route.data['create'];
    }

    for (const child of route.children) {
      res = {
        ...res,
        ...this.findRouteData(child)
      };
    }

    return res;
  }

  public editorConfigForNode(editor: NodeEditorConfig, path: Path) {
    const node = this.cms.tree.value.get(path);
    const rule = this.cms.config.findRule(node);

    const resource = editor.mode == Editor.Node ? undefined : ResourceForEditor[editor.mode];

    const res = {
      enabled: true,
      tooltip: undefined
    };
    if (rule && resource) {
      res.enabled = rule.resource.map(r => EditorForResource[r]).includes(editor.mode);
      if (rule.reason && rule.reason[resource]) {
        res.tooltip = rule.reason[resource];
      }
    }
    return res;
  }

  private canActivateEditor(mode: string, path: Path) {
    return this.cms
      .waitForNode(path)
      .then(() => {
        const editor = this.enabledEditors.find(e => e.mode == mode);
        const config = this.editorConfigForNode(editor, path);
        if (config.enabled) {
          return true;
        }
        return this.router.createUrlTree(['/node', path.uri()]);
      })
      .catch(err => {
        console.warn(err);
        return this.router.createUrlTree(['/home']);
      });
  }

  public async canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const data = this.findRouteData(state.root);
    if (data.path && data.path.uri() != this.path.value.uri()) {
      if (data.path?.language) {
        await this.cms.applyLanguage(data.path.language);
      }
      this.path.next(data.path);
    }
    if (data.mode && data.mode != this.mode.value) {
      this.mode.next(data.mode);
    }
    if (data.create != this.createRoute.value) {
      this.createRoute.next(data.create);
    }
    if (data.mode && data.path.length) {
      return this.canActivateEditor(this.mode.value, this.path.value);
    }
    return true;
  }
}
